import * as React from 'react';
import {Query, Mutation} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import gql from 'graphql-tag';

import type {
    SalesAutomationStage as SalesAutomationStageFragment,
    StagesetAndStages as StagesetAndStagesQuery,
    ActiveStage as ActiveStageQuery,
    ActiveStageVariables as ActiveStageQueryVariables,
    StageCreate as StageCreateMutation,
    StageCreateVariables as StageCreateMutationVariables,
    StageCreateInputFieldInput,
    StageUpdate as StageUpdateMutation,
    StageUpdateVariables as StageUpdateMutationVariables,
    StageUpdateInputFieldInput,
    StageDelete as StageDeleteMutation,
    StageDeleteVariables as StageDeleteMutationVariables,
    StageDeleteInputFieldInput,
} from 'nutshell-graphql-types';

import SALES_AUTOMATION_STAGE from '../graphql/fragments/sales-automation-stage.graphql';
import CREATE_STAGE from '../graphql/mutations/stage-create.graphql';
import DELETE_STAGE from '../graphql/mutations/stage-delete.graphql';
import UPDATE_STAGE from '../graphql/mutations/stage-update.graphql';
import GET_STAGESET_AND_STAGES from '../graphql/queries/get-stageset-and-stages.graphql';

import {FormStages} from './form-stages';
import type {StageFormPostData} from './form-stages';

type OwnProps = {
    entityId: string | null | undefined;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
};

type GraphQLProps = {
    stageUpdate: (arg1: StageUpdateInputFieldInput) => Promise<any>;
    stageDelete: (arg1: StageDeleteInputFieldInput) => Promise<any>;
    stageCreate: (arg1: StageCreateInputFieldInput) => Promise<any>;
    stage: SalesAutomationStageFragment | null | undefined;
};

type Props = OwnProps & GraphQLProps;

type RoutedProps = ContextRouter & OwnProps;

/*
 * Wrapper component around our stage form that hooks up to GraphQL
 * and passes down the relevant (cached) stage, and stage mutations:
 *
 * - StageUpdate
 */
export class FormStagesComponent extends React.PureComponent<Props> {
    render() {
        return (
            <FormStages
                {...this.props}
                stage={this.props.stage}
                onDelete={this.deleteForm}
                onSaveForm={this.saveForm}
            />
        );
    }

    saveForm = (formData: StageFormPostData) => {
        if (formData.id) {
            // @ts-expect-error
            return this.props.stageUpdate(formData);
        } else {
            // If there's no id, then we're creating a new stage
            // @ts-expect-error
            return this.props.stageCreate(formData);
        }
    };

    deleteForm = (entity: StageDeleteInputFieldInput) => {
        return this.props.stageDelete(entity);
    };
}

const GET_STAGE = gql`
    query ActiveStage($stageId: ID!) {
        stage(id: $stageId) {
            ...SalesAutomationStage
        }
    }
    ${SALES_AUTOMATION_STAGE}
`;

class StagesQuery extends Query<ActiveStageQuery, ActiveStageQueryVariables> {}
class CreateMutation extends Mutation<StageCreateMutation, StageCreateMutationVariables> {}
class UpdateMutation extends Mutation<StageUpdateMutation, StageUpdateMutationVariables> {}
class DeleteMutation extends Mutation<StageDeleteMutation, StageDeleteMutationVariables> {}

const FormStagesWrapperGraphQL = (props: RoutedProps) => (
    <StagesQuery
        query={GET_STAGE}
        variables={{stageId: props.entityId || ''}}
        skip={!props.entityId}
    >
        {({data}) => (
            <CreateMutation mutation={CREATE_STAGE}>
                {(createStage) => (
                    <UpdateMutation
                        mutation={UPDATE_STAGE}
                        update={(proxy, mutationResult) => {
                            if (!(mutationResult && mutationResult.data)) return;
                            const {stage} = mutationResult.data.stageUpdate;

                            // Write our data back to the cache.
                            proxy.writeFragment({
                                id: stage.id,
                                fragment: SALES_AUTOMATION_STAGE,
                                fragmentName: 'SalesAutomationStage',
                                data: stage,
                            });
                        }}
                    >
                        {(updateStage) => (
                            <DeleteMutation mutation={DELETE_STAGE}>
                                {(deleteStage) => {
                                    const stagesetId =
                                        props.match.params.stagesetId || '1-stagesets';

                                    return (
                                        <FormStagesComponent
                                            {...props}
                                            stage={data ? data.stage : null}
                                            stageCreate={(formData) => {
                                                const queriesToRefetch = [
                                                    {
                                                        query: GET_STAGESET_AND_STAGES,
                                                        variables: {stagesetId},
                                                    },
                                                ];

                                                const newStage = {
                                                    // @ts-expect-error
                                                    stagesetId,
                                                    ...formData,
                                                } as const;
                                                // @ts-expect-error
                                                delete newStage.id;

                                                return createStage({
                                                    variables: {
                                                        stage: newStage,
                                                    },
                                                    refetchQueries: queriesToRefetch,
                                                });
                                            }}
                                            stageUpdate={(formData) => {
                                                const queriesToRefetch = [
                                                    {
                                                        query: GET_STAGESET_AND_STAGES,
                                                        variables: {stagesetId},
                                                    },
                                                ];

                                                if (!formData.id) {
                                                    return Promise.reject(
                                                        'Cannot update a stage without an id'
                                                    );
                                                }

                                                return updateStage({
                                                    variables: {
                                                        stage: {
                                                            ...formData,
                                                            id: formData.id,
                                                        },
                                                    },
                                                    // Refetching stageset so the modified time updates
                                                    refetchQueries: queriesToRefetch,
                                                });
                                            }}
                                            stageDelete={(stageDeletePayload) => {
                                                return deleteStage({
                                                    variables: {
                                                        stage: stageDeletePayload,
                                                    },
                                                    update: (proxy) => {
                                                        // Read the data from our cache for this query.
                                                        const cacheResult:
                                                            | StagesetAndStagesQuery
                                                            | null
                                                            | undefined = proxy.readQuery({
                                                            query: GET_STAGESET_AND_STAGES,
                                                            variables: {
                                                                stagesetId: stagesetId,
                                                            },
                                                        });

                                                        if (!cacheResult || !cacheResult.stageset) {
                                                            return;
                                                        }
                                                        const cachedStageset = cacheResult.stageset;

                                                        // Filter out the stage that's being deleted
                                                        const newStages =
                                                            cachedStageset.stages.filter(
                                                                (stageToCheck) =>
                                                                    stageToCheck.id !==
                                                                    props.entityId
                                                            );
                                                        cachedStageset.stages = newStages;

                                                        // Write our data back to the cache.
                                                        proxy.writeQuery({
                                                            query: GET_STAGESET_AND_STAGES,
                                                            variables: {
                                                                stagesetId: stagesetId,
                                                            },
                                                            data: {
                                                                ...cacheResult,
                                                                stageset: cachedStageset,
                                                            },
                                                        });
                                                    },
                                                });
                                            }}
                                        />
                                    );
                                }}
                            </DeleteMutation>
                        )}
                    </UpdateMutation>
                )}
            </CreateMutation>
        )}
    </StagesQuery>
);

// @ts-expect-error
export const FormStagesWrapper = withRouter<OwnProps>(FormStagesWrapperGraphQL);
